import { useState, useRef } from 'react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { FileText, Layout, Palette, Layers, Upload, X, AlertCircle } from 'lucide-react'
import type { ScenarioInput } from '@creanizer/types'
import { readFileAsText, readMultipleFiles, formatFileSize, FILE_ACCEPT } from '../../utils/fileReader'

interface FormatAnalysisModeProps {
  input: ScenarioInput
  setInput: (input: ScenarioInput) => void
}

export function FormatAnalysisMode({ input, setInput }: FormatAnalysisModeProps) {
  const settings = input.formatAnalysisSettings || {
    sourceType: 'scenario',
    sourceText: '',
    extractStructure: true,
    extractStyle: true,
    extractPatterns: true
  }
  
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const updateSettings = (updates: Partial<ScenarioInput['formatAnalysisSettings']>) => {
    setInput({
      ...input,
      formatAnalysisSettings: {
        ...settings,
        ...updates
      }
    })
  }
  
  // ファイルアップロード処理
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return
    
    setIsProcessingFiles(true)
    setFileErrors([])
    
    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      if (errors.length > 0) {
        setFileErrors(errors)
      }
      
      if (combinedText) {
        // 既存のテキストがある場合は追加
        const newText = settings.sourceText 
          ? settings.sourceText + '\n\n---\n\n' + combinedText
          : combinedText
        updateSettings({ sourceText: newText })
        
        // 成功したファイルを保存
        const successFiles = fileInfos
          .filter(info => !info.error)
          .map((info, index) => files[index])
          .filter(Boolean)
        setUploadedFiles(prev => [...prev, ...successFiles])
      }
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      setFileErrors(['ファイルの読み込みに失敗しました'])
    } finally {
      setIsProcessingFiles(false)
      // ファイル入力をリセット
      if (event.target) {
        event.target.value = ''
      }
    }
  }
  
  // ファイル削除
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }
  
  return (
    <div className="space-y-6">
      <Alert>
        <FileText className="h-4 w-4" />
        <AlertDescription>
          既存のシナリオやテンプレートからフォーマット構造を解析し、保存します。
          保存したフォーマットはシナリオ生成時に利用できます。
        </AlertDescription>
      </Alert>
      
      <Card>
        <CardHeader>
          <CardTitle>フォーマット情報</CardTitle>
          <CardDescription>
            解析するフォーマットの基本情報を入力してください
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="format-name">フォーマット名（オプション）</Label>
            <Input
              id="format-name"
              value={settings.formatName || ''}
              onChange={(e) => updateSettings({ formatName: e.target.value })}
              placeholder="例: クトゥルフ神話TRPGシナリオ形式"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="format-description">説明（オプション）</Label>
            <Textarea
              id="format-description"
              value={settings.formatDescription || ''}
              onChange={(e) => updateSettings({ formatDescription: e.target.value })}
              placeholder="このフォーマットの特徴や用途を記述"
              rows={3}
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="source-type">ソースタイプ</Label>
            <Select 
              value={settings.sourceType} 
              onValueChange={(value) => updateSettings({ sourceType: value as any })}
            >
              <SelectTrigger id="source-type" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scenario">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    完成シナリオ
                  </div>
                </SelectItem>
                <SelectItem value="template">
                  <div className="flex items-center gap-2">
                    <Layout className="w-4 h-4" />
                    テンプレート
                  </div>
                </SelectItem>
                <SelectItem value="outline">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    アウトライン
                  </div>
                </SelectItem>
                <SelectItem value="mixed">
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    混合
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
              {settings.sourceType === 'scenario' && (
                <>完成したシナリオから構造とスタイルを抽出します。</>
              )}
              {settings.sourceType === 'template' && (
                <>シナリオテンプレートから形式と必須要素を抽出します。</>
              )}
              {settings.sourceType === 'outline' && (
                <>シナリオのアウトラインから基本構造を抽出します。</>
              )}
              {settings.sourceType === 'mixed' && (
                <>複数のフォーマットが混在したテキストから解析します。</>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>解析対象テキスト</CardTitle>
          <CardDescription>
            シナリオやテンプレートのテキストを入力してください
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* ファイルアップロードセクション */}
          <div className="space-y-3">
            <Label>ファイルから読み込み</Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPT}
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessingFiles}
              >
                <Upload className="w-4 h-4 mr-2" />
                ファイルを選択
              </Button>
              <p className="text-sm text-gray-500 mt-2">
                サポート形式：.txt, .docx, .pdf, .md
              </p>
              <p className="text-xs text-gray-400 mt-1">
                複数ファイルを選択できます
              </p>
            </div>
            
            {/* アップロード済みファイル一覧 */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">読み込み済みファイル</Label>
                <div className="space-y-1">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">{file.name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {formatFileSize(file.size)}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* エラー表示 */}
            {fileErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1">
                    {fileErrors.map((error, index) => (
                      <li key={index} className="text-sm">{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <div>
            <Label htmlFor="source-text">テキスト入力</Label>
            <Textarea
              id="source-text"
              value={settings.sourceText}
              onChange={(e) => updateSettings({ sourceText: e.target.value })}
              placeholder={getPlaceholderText(settings.sourceType)}
              rows={15}
              className="font-mono text-sm mt-2"
            />
            <p className="text-xs text-gray-500 mt-2">
              {settings.sourceText.length.toLocaleString()}文字
            </p>
          </div>
          
          <div className="space-y-3">
            <Label>解析オプション</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="extract-structure"
                  checked={settings.extractStructure}
                  onCheckedChange={(checked) => updateSettings({ extractStructure: checked === true })}
                />
                <Label htmlFor="extract-structure">
                  構造を抽出（セクション構成、流れ、ペーシング）
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="extract-style"
                  checked={settings.extractStyle}
                  onCheckedChange={(checked) => updateSettings({ extractStyle: checked === true })}
                />
                <Label htmlFor="extract-style">
                  文体を抽出（トーン、視点、語り口）
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="extract-patterns"
                  checked={settings.extractPatterns}
                  onCheckedChange={(checked) => updateSettings({ extractPatterns: checked === true })}
                />
                <Label htmlFor="extract-patterns">
                  パターンを抽出（定型表現、遷移、解決方法）
                </Label>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t space-y-3">
            <Label className="text-sm font-medium">AIモデル選択</Label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="useGemini3Mode"
                checked={settings.useGemini3Mode || false}
                onCheckedChange={(checked) => updateSettings({
                  useGemini3Mode: !!checked,
                  useGemini3FlashMode: checked ? false : settings.useGemini3FlashMode
                })}
              />
              <Label htmlFor="useGemini3Mode" className="text-sm">
                Gemini 3 モード（有料APIのみ・試験版）
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="useGemini3FlashMode"
                checked={settings.useGemini3FlashMode || false}
                onCheckedChange={(checked) => updateSettings({
                  useGemini3FlashMode: !!checked,
                  useGemini3Mode: checked ? false : settings.useGemini3Mode
                })}
              />
              <Label htmlFor="useGemini3FlashMode" className="text-sm">
                Gemini 3 Flash モード（有料APIのみ・試験版・高速）
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function getPlaceholderText(sourceType: string): string {
  switch (sourceType) {
    case 'scenario':
      return `完成したシナリオを貼り付けてください。

例:
【シナリオタイトル】
呪われた館の謎

【概要】
プレイヤーたちは、行方不明になった考古学者を探すため、
郊外の古い洋館を調査することになる。

【導入】
1920年代のアメリカ、マサチューセッツ州アーカム。
プレイヤーたちは新聞記事で、著名な考古学者エドワード・ブレイク教授が
3日前から行方不明になっていることを知る...

【第1シーン：調査開始】
...`
    
    case 'template':
      return `シナリオテンプレートを貼り付けてください。

例:
■シナリオ構成テンプレート■

1. タイトルとキャッチコピー
   - タイトル: [20文字以内]
   - キャッチコピー: [50文字以内の印象的なフレーズ]

2. 基本情報
   - プレイ時間: [時間]
   - プレイヤー人数: [人数]
   - 難易度: [初級/中級/上級]
   
3. 導入部
   - フック: [PCたちが関わる理由]
   - 初期情報: [最初に得られる情報]
...`
    
    case 'outline':
      return `シナリオのアウトラインを貼り付けてください。

例:
シナリオアウトライン

起: 
- 村で子供たちが次々と失踪
- 冒険者たちに調査依頼

承:
- 森の奥の廃城が怪しいという情報
- 廃城への道中で罠や魔物と遭遇

転:
- 廃城で魔術師が子供たちを実験に使用
- 実は魔術師も何者かに操られている

結:
- 真の黒幕との対決
- 子供たちの救出
...`
    
    default:
      return 'フォーマットに関するテキストを入力してください'
  }
}